import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import TimeRequestService, { TimeRequest } from './TimeRequestService';
import { requestCellKey } from './dayLockHelpers';

export const TIME_REQUESTS_QUERY_KEY = ['timesheetTimeRequests'] as const;

const NONE: TimeRequest[] = [];

/** Invalidate every time-request query (panel, badge, cell colours) after a mutation. */
export function useInvalidateTimeRequests() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: TIME_REQUESTS_QUERY_KEY }),
    [queryClient],
  );
}

/**
 * Pending requests NOT filtered by week, so one call drives both the admin Requests panel and its
 * badge across every week (plan F1). `matterId` narrows it to one project (BillingTimesheetView).
 */
export function usePendingTimeRequests(enabled: boolean, matterId?: string | null) {
  const query = useQuery({
    queryKey: [...TIME_REQUESTS_QUERY_KEY, 'pending', matterId ?? 'all'],
    queryFn: () => TimeRequestService.list({ status: 'pending', matterid: matterId ?? undefined }),
    enabled,
    refetchOnWindowFocus: true,
  });
  return { requests: enabled && query.data ? query.data : NONE, isLoading: query.isLoading };
}

/**
 * The caller's requests for the visible week (every status), reduced to the LATEST request per grid
 * cell — that is what colours a permish 2/3 user's cells (pending amber, declined red outline).
 */
export function useWeekTimeRequests(
  enabled: boolean,
  from: string,
  to: string,
  matterId?: string | null,
) {
  const query = useQuery({
    queryKey: [...TIME_REQUESTS_QUERY_KEY, 'range', from, to, matterId ?? 'all'],
    queryFn: () => TimeRequestService.list({ from, to, matterid: matterId ?? undefined }),
    enabled,
    refetchOnWindowFocus: true,
  });
  const data = enabled && query.data ? query.data : NONE;
  return useMemo(() => {
    const latest = new Map<string, TimeRequest>();
    // The API returns newest first, so the first request seen for a cell is its latest.
    data.forEach((r) => {
      const key = requestCellKey(r.employeeId, r.matterId, r.costCode, r.workDate);
      if (!latest.has(key)) latest.set(key, r);
    });
    return latest;
  }, [data]);
}
